import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { examDate } from "../content/plan-constants";
import { dueMistakes } from "../domain/rules";
import { daysBetween, todayInShanghai } from "../lib/format";
import { useStudy } from "../state/StudyContext";
import { StatusMessage } from "../components/ui";
import { PwaStatus } from "./PwaStatus";
import { useDailyReminders } from "./reminders";
import { APP_VERSION } from "./version";
import { maskAccountIdentifier } from "./auth-session";

export const DEFAULT_ENTRY_PATH = "/calendar";

export const primaryNavigation = [
  { to: "/calendar", label: "课程", glyph: "程" },
  { to: "/knowledge", label: "知识库", glyph: "知" },
  { to: "/progress", label: "进度", glyph: "进" },
  { to: "/mistakes", label: "错题", glyph: "错" },
  { to: "/help", label: "指南", glyph: "问" },
] as const;

const AUTO_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_SYNC_DEBOUNCE_MS = 3 * 1000;

function Navigation({ mobile = false }: { mobile?: boolean }) {
  return (
    <nav className={mobile ? "mobile-navigation" : "desktop-navigation"} aria-label={mobile ? "移动端主导航" : "主导航"}>
      {primaryNavigation.map((item) => (
        <NavLink key={item.to} className="app-nav-link" to={item.to}>
          <span className="app-nav-link__glyph" aria-hidden="true">{item.glyph}</span>
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function StudyRouteOutlet({ ready }: { ready: boolean }) {
  if (!ready) return <div className="app-loading-bar" aria-label="正在载入本机学习数据" />;
  return <Outlet />;
}

export function AppShell() {
  const location = useLocation();
  const {
    ready,
    mistakes,
    notice,
    clearNotice,
    pendingSyncCount,
    syncResult,
    syncNow,
  } = useStudy();
  const autoSyncRunningRef = useRef(false);
  const requestAutoSyncRef = useRef<() => void>(() => undefined);
  const authClientRef = useRef<SupabaseClient | undefined>(undefined);
  const [cloudSession, setCloudSession] = useState<Session | null>(null);
  const [authConfigured, setAuthConfigured] = useState<boolean>();
  const [signingOut, setSigningOut] = useState(false);
  const today = todayInShanghai();
  const dueReviewCount = dueMistakes(mistakes, today).length;
  const countdown = Math.max(0, daysBetween(today, examDate));

  useDailyReminders(dueReviewCount);

  useEffect(() => {
    let active = true;
    let authenticated = false;
    let interval: number | undefined;
    let unsubscribeAuth: () => void = () => undefined;

    const runAutoSync = async () => {
      if (!active || !authenticated || autoSyncRunningRef.current
        || !navigator.onLine || document.visibilityState === "hidden") return;
      autoSyncRunningRef.current = true;
      try {
        await syncNow({ silent: true });
      } catch (error) {
        console.warn("后台自动同步失败", error);
      } finally {
        autoSyncRunningRef.current = false;
      }
    };

    requestAutoSyncRef.current = () => { void runAutoSync(); };
    const handleOnline = () => { void runAutoSync(); };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void runAutoSync();
    };

    void import("../data/supabase").then(({ createSupabaseClientFromEnvironment }) => {
      if (!active) return;
      const client = createSupabaseClientFromEnvironment();
      setAuthConfigured(Boolean(client));
      if (!client) return;
      authClientRef.current = client;
      void client.auth.getSession().then(({ data }) => {
        if (!active) return;
        setCloudSession(data.session);
        authenticated = Boolean(data.session);
        if (authenticated) window.setTimeout(() => { void runAutoSync(); }, 0);
      });
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        setCloudSession(session);
        authenticated = Boolean(session);
        if (authenticated) window.setTimeout(() => { void runAutoSync(); }, 0);
      });
      unsubscribeAuth = () => data.subscription.unsubscribe();
      interval = window.setInterval(() => { void runAutoSync(); }, AUTO_SYNC_INTERVAL_MS);
      window.addEventListener("online", handleOnline);
      document.addEventListener("visibilitychange", handleVisibility);
    }).catch((error) => console.warn("自动同步模块加载失败", error));

    return () => {
      active = false;
      authClientRef.current = undefined;
      requestAutoSyncRef.current = () => undefined;
      if (interval !== undefined) window.clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      unsubscribeAuth();
    };
  }, [syncNow]);

  useEffect(() => {
    if (!pendingSyncCount) return;
    const timeout = window.setTimeout(() => requestAutoSyncRef.current(), AUTO_SYNC_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [pendingSyncCount]);

  useEffect(() => {
    const main = document.getElementById("main-content");
    if (main) {
      main.dataset.focusOrigin = "route";
      main.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const skipToMain = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const main = document.getElementById("main-content");
    if (main) main.dataset.focusOrigin = "skip";
    main?.focus({ preventScroll: true });
    main?.scrollIntoView({ block: "start" });
  };

  const signOutFromTopbar = async () => {
    const client = authClientRef.current;
    if (!client || signingOut) return;
    setSigningOut(true);
    try {
      const { error } = await client.auth.signOut();
      if (error) console.warn("退出云端账号失败", error);
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="app-shell">
      <button className="skip-link" onClick={skipToMain} type="button">跳到主要内容</button>
      <header className="app-topbar" data-auth-state={cloudSession ? "signed-in" : "signed-out"}>
        <Link className="app-brand" to={DEFAULT_ENTRY_PATH} aria-label="核安备考学习中心课程首页">
          <span className="app-brand__mark" aria-hidden="true">NS</span>
          <span>
            <span className="app-kicker">Nuclear Safety · 2026</span>
            <span className="app-title">核安备考学习中心</span>
          </span>
        </Link>
        <div className="app-topbar__status" aria-label="应用状态">
          <span className="topbar-stat"><b data-numeric>{countdown}</b>天冲刺</span>
          <span className="topbar-stat" data-state={pendingSyncCount ? "pending" : "ok"}>
            {syncResult?.mode === "synced" && !pendingSyncCount ? "云端已同步" : pendingSyncCount ? `${pendingSyncCount}项待同步` : "本机已保存"}
          </span>
          {cloudSession ? (
            <span className="account-chip" data-state="signed-in">
              <Link aria-label="查看当前登录账号与同步设置" className="account-chip__identity" to="/settings/data">
                <span aria-hidden="true">✓</span>
                <span>已登录 · {maskAccountIdentifier(cloudSession.user.email, cloudSession.user.id)}</span>
              </Link>
              <button disabled={signingOut} onClick={() => void signOutFromTopbar()} type="button">
                {signingOut ? "退出中" : "退出"}
              </button>
            </span>
          ) : (
            <Link className="account-chip account-chip--login" data-state="signed-out" to="/settings/data">
              {authConfigured === false ? "离线使用" : "登录"}
            </Link>
          )}
          <Link className="settings-link" to="/settings/data" aria-label="打开数据与同步设置">设置</Link>
        </div>
      </header>

      <PwaStatus />

      <div className="app-layout">
        <aside className="app-sidebar">
          <Navigation />
          <div className="sidebar-brief">
            <span>今日回炉</span>
            <strong data-numeric>{dueReviewCount}</strong>
            <p>D1 / D3 / D7 到期错题</p>
            <Link to="/mistakes">进入回炉队列 →</Link>
          </div>
          <div className="sidebar-rule">
            <span>执行原则</span>
            <p>计时可浮动，验收证据不可省。晚间复盘仍回到 Codex。</p>
          </div>
        </aside>

        <main id="main-content" tabIndex={-1}>
          {notice ? (
            <StatusMessage
              className="global-notice"
              tone={notice.tone === "error" ? "danger" : notice.tone === "success" ? "info" : notice.tone}
            >
              <span>{notice.message}</span>
              <button className="notice-dismiss" onClick={clearNotice} type="button">关闭</button>
            </StatusMessage>
          ) : null}
          <StudyRouteOutlet ready={ready} />
        </main>
      </div>

      <footer className="app-footer">
        <p>本机优先保存；只有云端返回成功才显示“已同步”。教材文件始终留在你的设备中。</p>
        <span className="app-footer__meta">
          <Link to="/help">使用指南</Link>
          <Link to="/settings/data">备份与恢复</Link>
          <time className="app-version" dateTime={APP_VERSION.dateTime} title="最新修改日期和时间">
            版本 {APP_VERSION.label}
          </time>
        </span>
      </footer>

      <Navigation mobile />
    </div>
  );
}
